import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, CheckCircle2, Circle, Trash2, Calendar } from "lucide-react";

export default function Tasks() {
  const nav = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", due_date: "", priority: "medium", prospect_id: "" });
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/tasks").then((r) => setTasks(r.data));

  useEffect(() => {
    load();
    api.get("/prospects").then((r) => setProspects(r.data));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title || !form.due_date) return toast.error("Titre et date requis");
    try {
      await api.post("/tasks", { ...form, prospect_id: form.prospect_id || null });
      toast.success("Relance ajoutée");
      setForm({ title: "", description: "", due_date: "", priority: "medium", prospect_id: "" });
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const toggle = async (t) => {
    await api.patch(`/tasks/${t.task_id}`, { completed: !t.completed });
    load();
  };

  const del = async (t) => {
    await api.delete(`/tasks/${t.task_id}`);
    load();
  };

  const filtered = tasks.filter((t) => filter === "all" ? true : filter === "todo" ? !t.completed : t.completed);
  const today = new Date().toISOString().slice(0, 10);

  const priorityColors = {
    high: "border-rose-500/40 bg-rose-500/10 text-rose-300",
    medium: "border-amber-500/40 bg-amber-500/10 text-amber-300",
    low: "border-slate-700 bg-slate-800/40 text-slate-400",
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px]">
      <header className="mb-6">
        <div className="text-xs font-mono uppercase tracking-widest text-cyan-400/80 mb-2">// Relances</div>
        <h1 className="font-heading text-4xl font-extrabold text-slate-100 tracking-tight">Tâches & relances</h1>
        <p className="text-slate-400 mt-1.5 text-sm">Ne ratez aucun rappel client.</p>
      </header>

      <form onSubmit={create} className="p-6 bg-[#121215] border border-slate-800/80 rounded-xl mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Plus className="w-4 h-4 text-cyan-400" />
          <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Nouvelle relance</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <input
            data-testid="task-title-input"
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Titre (Rappeler M. Dupont)"
            className="md:col-span-2 px-3 py-2.5 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 text-sm outline-none focus:border-cyan-500/60"
          />
          <input
            data-testid="task-date-input"
            required
            type="date"
            value={form.due_date}
            min={today}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            className="px-3 py-2.5 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 text-sm outline-none focus:border-cyan-500/60"
          />
          <select
            data-testid="task-priority-select"
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
            className="px-3 py-2.5 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 text-sm outline-none"
          >
            <option value="low">Basse</option>
            <option value="medium">Moyenne</option>
            <option value="high">Haute</option>
          </select>
          <select
            data-testid="task-prospect-select"
            value={form.prospect_id}
            onChange={(e) => setForm({ ...form, prospect_id: e.target.value })}
            className="px-3 py-2.5 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 text-sm outline-none"
          >
            <option value="">Prospect (opt.)</option>
            {prospects.map((p) => <option key={p.prospect_id} value={p.prospect_id}>{p.company}</option>)}
          </select>
          <button data-testid="task-submit-button" className="px-4 py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold text-sm transition-colors">
            Ajouter
          </button>
        </div>
      </form>

      <div className="flex items-center gap-2 mb-4">
        {[
          ["all", "Toutes"],
          ["todo", "À faire"],
          ["done", "Terminées"],
        ].map(([k, l]) => (
          <button
            key={k}
            data-testid={`filter-${k}`}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === k ? "bg-cyan-500/15 text-cyan-300 border border-cyan-500/30" : "text-slate-400 hover:text-slate-200 border border-slate-800"}`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {filtered.map((t) => {
          const overdue = !t.completed && t.due_date && t.due_date < today;
          const prospect = prospects.find((p) => p.prospect_id === t.prospect_id);
          return (
            <div key={t.task_id} data-testid={`task-${t.task_id}`} className={`p-4 bg-[#121215] border rounded-xl flex items-center gap-4 ${overdue ? "border-rose-500/40" : "border-slate-800/80"}`}>
              <button data-testid={`toggle-task-${t.task_id}`} onClick={() => toggle(t)}>
                {t.completed ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <Circle className="w-5 h-5 text-slate-500 hover:text-cyan-400" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`font-medium text-sm ${t.completed ? "line-through text-slate-500" : "text-slate-100"}`}>{t.title}</div>
                {t.description && <div className="text-xs text-slate-500 mt-0.5">{t.description}</div>}
                <div className="flex items-center gap-3 mt-1.5 text-xs">
                  <span className={`flex items-center gap-1 font-mono ${overdue ? "text-rose-400" : "text-slate-500"}`}>
                    <Calendar className="w-3 h-3" />
                    {new Date(t.due_date).toLocaleDateString("fr-FR")}
                    {overdue && " (en retard)"}
                  </span>
                  {prospect && (
                    <button onClick={() => nav(`/prospects/${prospect.prospect_id}`)} className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2">
                      {prospect.company}
                    </button>
                  )}
                </div>
              </div>
              <span className={`text-xs font-mono px-2 py-0.5 rounded-full border ${priorityColors[t.priority]}`}>{t.priority}</span>
              <button data-testid={`delete-task-${t.task_id}`} onClick={() => del(t)} className="text-slate-500 hover:text-rose-400">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-slate-500 font-mono text-sm">Aucune tâche</div>
        )}
      </div>
    </div>
  );
}
